/**
 * @version 1.0.0
 * @created 2026-02-02
 * @description [Service] Supabase database helper functions
 *              User profiles, session management, and audit logging
 */
import { createAnonClient, createSSRClient } from "./server";
// Soruces
import type { NextContext } from "@/source/types";

type Schemas = "bz_user" | "bz_reservation" | "bz_finance" | "bz_security" | "bz_staging";

// RPCs returning { success, message?, data? }
const MUTATION_PREFIXES = ["update_", "create_", "delete_", "match_", "upsert_", "approve_", "revoke_"];

/**
 * Execute Supabase RPC with SSR client and unified error handling.
 * - get_*: returns { data: data ?? [], error } — 빈 배열이면 error "조회 내역이 없습니다.", 아니면 null
 * - update_/create_/delete_/match_/upsert_*: throws on null data or success === false
 * - others: returns data as-is
 *
 * @param context - Next.js SSR/API context
 * @param schema - Database schema
 * @param fn - RPC function name
 * @param params - RPC parameters
 */
export async function executeRPC<T = any>(
  context: NextContext, 
  schema: Schemas, 
  fn: `get_${string}`, 
  params?: Record<string, unknown>
): Promise<{ data: T; error: string | null }>;

export async function executeRPC<T = any>(
  context: NextContext, 
  schema: Schemas, 
  fn: string, 
  params?: Record<string, unknown>
): Promise<T>;

export async function executeRPC(
  context: NextContext, 
  schema: Schemas, 
  fn: string, 
  params?: Record<string, unknown>
): Promise<any> {
  
  const supabase = createSSRClient(context);
  const { data, error } = await supabase.schema(schema).rpc(fn, params);

  if (error) throw new Error(`[SERVICE] ${fn}: ${error.message}`);

  if (fn.startsWith("get_")) {
    const result = data ?? [];

    return { data: result, error: Array.isArray(result) && result.length === 0 ? "조회 내역이 없습니다." : null };
  }

  if (MUTATION_PREFIXES.some((prefix) => fn.startsWith(prefix))) {
    if (data == null) throw new Error(`[SERVICE] ${fn}: No data returned`);
    if (data.success === false) throw Object.assign(new Error(data.message ?? `[SERVICE] ${fn} failed`), { data: data.data });
  }

  return data;
}

/**
 * Get user profile from Supabase by user ID
 *
 * @param userId - Supabase auth user ID
 * @returns User profile data
 * @throws Error if profile not found or database error
 */
export async function getProfileById(userId: string) {

  const supabase = createAnonClient();
  const { data, error } = await supabase.schema("bz_user").rpc("get_profile_by_user_id", { p_auth_id: userId });

  if (error) { return null; }

  return data;
}
